/**
 * 마크다운 형식의 텍스트를 HTML로 변환하는 유틸리티 함수
 * @param {string} text - 변환할 텍스트
 * @param {boolean} isDevMode - 개발자 모드 여부 (기본값: false)
 * @returns {string} HTML로 변환된 텍스트
 */
export const formatMarkdownLike = (text, isDevMode = false) => {
  if (!text) return '';
  
  // 개발자 모드가 아닌 경우, 도구 호출 및 결과 블록 제거
  if (!isDevMode) {
    // 기존 도구 호출 및 결과 관련 코드 블록 제거
    text = text.replace(/```sql-query[\s\S]*?```/g, '');
    text = text.replace(/```json\n# #\d+번.*?결과[\s\S]*?```/g, '');
    text = text.replace(/```json\n# #\d+번.*?쿼리[\s\S]*?```/g, '');
    text = text.replace(/```diff\n# #\d+번.*?결과[\s\S]*?```/g, '');
    
    // 추가: 메타데이터 블록들 제거
    text = text.replace(/```metadata[\s\S]*?```/g, '');
    
    // 추가: 원본 도구 결과 블록 제거
    text = text.replace(/```json\n# 🔧 원본 도구 결과 \(Raw Result\)[\s\S]*?```/g, '');
    
    // 추가: 쿼리 결과 정보 블록들 제거 (다양한 패턴)
    text = text.replace(/```json\n# 📈 쿼리 결과 정보 \(분석\)[\s\S]*?```/g, '');
    text = text.replace(/```json\n# 📈 쿼리 결과 정보[\s\S]*?```/g, '');
    
    // 추가: 일반적인 도구 실행 관련 JSON 블록들 제거
    text = text.replace(/```json\n# .*?도구.*?[\s\S]*?```/g, '');
    text = text.replace(/```json\n# .*?결과.*?[\s\S]*?```/g, '');
    text = text.replace(/```json\n# .*?쿼리.*?[\s\S]*?```/g, '');
    
    // 연속된 줄바꿈 정리 (3개 이상 연속된 줄바꿈을 2개로 축소)
    text = text.replace(/\n{3,}/g, '\n\n');
    
    // 빈 줄로만 이루어진 텍스트 정리
    text = text.replace(/^\s*$/gm, '');
    text = text.replace(/\n{3,}/g, '\n\n');
  } else {
    // 개발자 모드에서는 SQL 블록과 메타데이터를 더 잘 보이도록 처리
    // SQL 쿼리 블록 스타일링 개선
    text = text.replace(/```sql-query([\s\S]*?)```/g, '<div class="dev-sql-block"><div class="dev-block-header">🔍 SQL Query</div><pre class="dev-sql-content"><code>$1</code></pre></div>');
    
    // JSON 결과 블록 스타일링 개선
    text = text.replace(/```json\n# #(\d+번.*?결과)([\s\S]*?)```/g, '<div class="dev-result-block"><div class="dev-block-header">📊 $1</div><pre class="dev-json-content"><code>$2</code></pre></div>');
    
    // 원본 도구 결과 블록 스타일링 (회색 테마 사용)
    text = text.replace(/```json\n# 🔧 원본 도구 결과 \(Raw Result\)\n([\s\S]*?)```/g, '<div class="dev-metadata-block"><div class="dev-block-header">🔧 원본 도구 결과 (Raw Result)</div><pre class="dev-metadata-content"><code>$1</code></pre></div>');
    
    // 쿼리 결과 정보 (분석) 블록 스타일링 (초록색 테마 사용)
    text = text.replace(/```json\n# 📈 쿼리 결과 정보 \(분석\)\n([\s\S]*?)```/g, '<div class="dev-result-block"><div class="dev-block-header">📈 쿼리 결과 정보 (분석)</div><pre class="dev-json-content"><code>$1</code></pre></div>');
    
    // 기존 쿼리 결과 정보 블록 스타일링 (하위 호환성)
    text = text.replace(/```json\n# 📈 쿼리 결과 정보\n([\s\S]*?)```/g, '<div class="dev-result-block"><div class="dev-block-header">📈 쿼리 결과 정보</div><pre class="dev-json-content"><code>$1</code></pre></div>');
    
    // JSON 쿼리 블록 스타일링 개선  
    text = text.replace(/```json\n# #(\d+번.*?쿼리)([\s\S]*?)```/g, '<div class="dev-query-block"><div class="dev-block-header">⚙️ $1</div><pre class="dev-json-content"><code>$2</code></pre></div>');
    
    // DIFF 결과 블록 스타일링 개선
    text = text.replace(/```diff\n# #(\d+번.*?결과)([\s\S]*?)```/g, '<div class="dev-diff-block"><div class="dev-block-header">📈 $1</div><pre class="dev-diff-content"><code>$2</code></pre></div>');
    
    // 메타데이터 블록 추가 (일반적인 메타데이터 정보)
    text = text.replace(/```metadata\n([\s\S]*?)```/g, '<div class="dev-metadata-block"><div class="dev-block-header">🔧 Metadata</div><pre class="dev-metadata-content"><code>$1</code></pre></div>');
  }
  
  // 일반 코드 블록 처리 (```code```) - 개발자 블록이 아닌 경우
  text = text.replace(/```(?!sql-query|json|diff|metadata)([\w]*)\n?([\s\S]*?)```/g, '<pre class="bg-gray-800 text-white p-2 rounded overflow-auto"><code class="language-$1">$2</code></pre>');
  
  // 인라인 코드 처리 (`code`)
  text = text.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>');
  
  // 헤더 처리
  text = text.replace(/^# (.*$)/gm, '<h1 class="text-xl font-bold mt-2 mb-1">$1</h1>');
  text = text.replace(/^## (.*$)/gm, '<h2 class="text-lg font-bold mt-2 mb-1">$1</h2>');
  text = text.replace(/^### (.*$)/gm, '<h3 class="text-md font-bold mt-2 mb-1">$1</h3>');
  
  // 굵은 텍스트 처리
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // 기울임 텍스트 처리
  text = text.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // 목록 처리를 더 세밀하게
  // 먼저 목록 블록을 찾아서 처리
  let listPattern = /^(\s*-\s+.*(?:\n\s*-\s+.*)*)/gm;
  text = text.replace(listPattern, function(match) {
    // 각 라인을 li로 변환하되 줄바꿈 제거
    let listItems = match.split(/\n\s*-\s+/);
    let firstItem = listItems.shift().replace(/^\s*-\s+/, '');
    let html = `<ul class="compact-list"><li>${firstItem}</li>`;
    
    if (listItems.length > 0) {
      html += listItems.map(item => `<li>${item}</li>`).join('');
    }
    
    html += '</ul>';
    return html;
  });
  
  // 그 외의 남은 개별 목록 항목 처리
  text = text.replace(/^\s*-\s+(.*$)/gm, '<ul class="compact-list"><li>$1</li></ul>');
  
  // 불필요한 중첩 제거
  text = text.replace(/<\/ul>\s*<ul class="compact-list">/g, '');
  
  // 링크 처리 - target="_blank"와 rel="noopener noreferrer" 추가
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-500 underline">$1</a>');
  
  // 줄바꿈 처리 - 개조식 목록에서 줄간격 최소화
  text = text.replace(/<\/li><br\/><li/g, '</li><li');
  text = text.replace(/\n/g, '<br/>');
  
  return text;
};

export default formatMarkdownLike; 